import ClipboardIcon from "@/assets/colorable/clipboard.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import Button from "@/components/Common/Button";
import clsxm from "@/lib/clsxm";
import {ReactNode, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce, useMedia} from "react-use";

const CopyToClipboardButton: FC<{
  collapseIcon?: ReactNode;
  collapseOnSmallerScreen?: boolean;
  contentToCopy: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  label: string;
  icon?: ReactNode;
  shouldKeepStyleOnSuccess?: boolean;
  successLabel?: string;
  className?: string;
  buttonStyle?: "Gradient" | "Blue" | "White" | "Custom";
  onCopyCallback?: () => void;
}> = ({
  collapseIcon,
  collapseOnSmallerScreen,
  contentToCopy,
  isDisabled,
  isLoading,
  label,
  icon,
  shouldKeepStyleOnSuccess,
  successLabel,
  className,
  buttonStyle = "White",
  onCopyCallback,
}) => {
  const {t} = useTranslation("common");
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const isUnderMd = useMedia("(max-width: 768px)");

  useDebounce(
    () => {
      if (showSuccessMessage) setShowSuccessMessage(false);
    },
    1000,
    [showSuccessMessage],
  );

  const shouldShowSuccessStyle = showSuccessMessage && !shouldKeepStyleOnSuccess;

  return (
    <Button
      buttonStyle={shouldShowSuccessStyle ? "Custom" : buttonStyle}
      isDisabled={isDisabled || showSuccessMessage}
      isLoading={isLoading}
      onClick={() => {
        navigator.clipboard.writeText(contentToCopy);
        setShowSuccessMessage(true);
        onCopyCallback?.();
      }}
      className={clsxm("w-full gap-2 p-2 font-medium whitespace-normal", className, {
        "bg-primary-powder text-success-medium-green border-none": shouldShowSuccessStyle,
      })}>
      {collapseOnSmallerScreen && isUnderMd ? (
        showSuccessMessage ? (
          <TickIcon className="border-success-medium-green h-5 w-auto shrink-0 rounded-full border p-1" />
        ) : (
          (collapseIcon ?? icon ?? <ClipboardIcon className="h-4 w-auto shrink-0" />)
        )
      ) : (
        <>
          {showSuccessMessage ? (
            <TickIcon className="border-success-medium-green h-5 w-auto shrink-0 rounded-full border p-1" />
          ) : icon !== undefined ? (
            icon
          ) : (
            <ClipboardIcon className="h-4 w-auto shrink-0" />
          )}
          <span className="min-w-0 truncate">{showSuccessMessage ? (successLabel ?? t("copied")) : label}</span>
        </>
      )}
    </Button>
  );
};

export default CopyToClipboardButton;
